"use client";

import { Check, Megaphone } from "lucide-react";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  ALL_PLAN_FEATURES,
  FEATURES_SCOPE_LABEL,
  SHOW_FEATURED_SPOT_UI,
} from "@/lib/features";
import { promotions } from "@/config/promotions";

type PlanFeature = {
  label: string;
  included: boolean;
};

type Plan = {
  headline: string;
  subheadline: string;
  price: string;
  pricePeriod?: string;
  originalPrice?: string;
  ctaLabel: string;
  features: PlanFeature[];
  highlight?: boolean;
  isSubscription?: boolean;
  planKey: "monthly" | "lifetime";
};

const plans: Plan[] = [
  {
    headline: "Monthly",
    subheadline: "$5/month",
    price: "$5",
    pricePeriod: "/month",
    ctaLabel: "Subscribe Monthly",
    features: [],
    isSubscription: true,
    planKey: "monthly",
  },
  {
    headline: "Lifetime",
    subheadline: "$35 one time payment",
    price: "$35",
    ctaLabel: "Get Lifetime Access",
    features: [],
    highlight: true,
    planKey: "lifetime",
  },
];

export function Pricing() {
  const showBlackFridayPromo = promotions.blackFridayEnabled;
  const displayPlans = plans.map((plan) => {
    if (!showBlackFridayPromo || plan.planKey !== "lifetime") {
      return plan;
    }

    return {
      ...plan,
      subheadline: "$25 one time payment",
      price: "$25",
      originalPrice: "$35",
    };
  });

  const redirectToSignin = (plan: Plan["planKey"]) => {
    const callbackUrl = `/checkout?plan=${plan}`;
    const encodedCallbackUrl = encodeURIComponent(callbackUrl);
    window.location.href = `/signin?callbackUrl=${encodedCallbackUrl}`;
  };

  return (
    <section className="pt-24 pb-16">
      <div className="mx-auto max-w-7xl">
        <header className="mb-12 text-center">
          <div className="inline-flex items-center px-3 py-1 rounded-full border border-[#10b981] mb-4">
            <span className="text-xs font-medium text-[#10b981] uppercase tracking-wider">
              Pricing
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Get your website to
            <span className="block text-[#10b981] pb-1">the next level</span>
          </h2>
        </header>

        {/* Combined Layout */}
        <div className="rounded-2xl border border-border/60 p-8 sm:p-12">
          <div className="grid w-full gap-8 max-[1200px]:grid-cols-1 min-[1200px]:grid-cols-[1fr_2fr]">
            {/* Left: Features */}
            <div>
              <p className="mb-6 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                {FEATURES_SCOPE_LABEL}
              </p>
              <ul className="grid grid-cols-1 gap-4 max-[870px]:grid-cols-1 min-[870px]:max-[1200px]:grid-cols-2 min-[1200px]:grid-cols-1">
                {ALL_PLAN_FEATURES.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-[#10b981] bg-[#10b981]/10 text-[#10b981]">
                      <Check className="h-3 w-3" strokeWidth={2.5} />
                    </span>
                    <span className="text-sm leading-relaxed text-foreground sm:text-base">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Plans */}
            <div className="grid gap-6 max-[840px]:grid-cols-1 min-[840px]:grid-cols-2 items-center">
              {displayPlans.map((plan) => (
                <div
                  key={plan.headline}
                  className={cn(
                    "relative flex flex-col gap-6 rounded-2xl border bg-card/80 p-8 text-center sm:p-10",
                    plan.highlight
                      ? "border-[#10b981]/60"
                      : "border-border/60 min-[840px]:p-6 min-[840px]:sm:p-8 min-[840px]:self-center"
                  )}
                >
                  {plan.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full border border-[#10b981] bg-background text-xs font-medium text-[#10b981] uppercase tracking-wider">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className="space-y-3">
                    <h3 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                      {plan.headline}
                    </h3>
                    <div className="flex flex-col items-center gap-2">
                      {plan.highlight &&
                        showBlackFridayPromo &&
                        plan.originalPrice && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-amber-500 bg-amber-500/10 text-xs font-medium text-amber-600 dark:text-amber-500 uppercase tracking-wider">
                            Black Friday Deal
                          </span>
                        )}
                      <div className="flex items-baseline justify-center gap-2">
                        {plan.originalPrice && (
                          <span className="text-lg text-muted-foreground line-through">
                            {plan.originalPrice}
                          </span>
                        )}
                        <span className="text-4xl font-semibold text-foreground sm:text-5xl">
                          {plan.price}
                        </span>
                        {plan.highlight && !plan.pricePeriod && (
                          <span className="text-sm text-muted-foreground">
                            One time
                          </span>
                        )}
                        {plan.pricePeriod && (
                          <span className="text-base text-muted-foreground">
                            {plan.pricePeriod}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Button
                      onClick={() => redirectToSignin(plan.planKey)}
                      size="lg"
                      className="w-full rounded-xl text-sm font-medium"
                    >
                      {plan.ctaLabel}
                    </Button>
                    {plan.highlight && (
                      <p className="text-xs text-muted-foreground">
                        No subscription • One time payment
                      </p>
                    )}
                    {plan.isSubscription && (
                      <p className="text-xs text-muted-foreground">
                        Cancel anytime
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {SHOW_FEATURED_SPOT_UI && (
        <div className="mt-12">
          <div className="mx-auto max-w-3xl rounded-2xl border border-border/60 bg-card/80 p-8 text-center sm:p-10">
            <div className="mb-4 inline-flex items-center px-3 py-1 rounded-full border border-[#10b981]">
              <Megaphone className="mr-1.5 h-3.5 w-3.5 text-[#10b981]" />
              <span className="text-xs font-medium text-[#10b981] uppercase tracking-wider">
                Featured Spot
              </span>
            </div>
            <h3 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              Be seen where it matters
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Your website will be featured on the landing page and in the
              leaderboard
            </p>
            <div className="mt-4 mb-6 flex items-baseline justify-center gap-2">
              <span className="text-4xl font-semibold text-foreground sm:text-5xl">
                $25
              </span>
              <span className="text-base text-muted-foreground">/month</span>
            </div>
            <Button asChild size="lg" className="rounded-xl text-sm font-medium">
              <Link href="/featured">Get Featured</Link>
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

export default Pricing;
